import { useEffect, useMemo, useState } from "react";
import {
  Box, Sheet, Card, Typography, Input, Textarea, Button, Chip, Alert,
  CircularProgress, Divider, Select, Option, Checkbox,
  FormControl, FormLabel, FormHelperText, Tabs, TabList, Tab,
} from "@mui/joy";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { adminApi } from "../adminApi";
import { useAdminAuth } from "../AdminAuthContext";

const DANGER_COLOR = { low: "success", medium: "warning", high: "danger" };
const DANGER_LABEL = { low: "безопасно", medium: "внимание", high: "осторожно" };
const ROLE_LABEL = { superadmin: "суперадмин", admin: "админ" };

// Начальные значения формы по схеме параметров действия
function initialForm(action) {
  const f = {};
  for (const p of action.params) {
    if (p.kind === "flag" || p.type === "bool") f[p.name] = !!p.default;
    else if (p.kind === "bool_optional") f[p.name] = "";
    else f[p.name] = p.default != null ? String(p.default) : "";
  }
  return f;
}

// Сборка объекта params для запроса (пустые необязательные не отправляем)
function buildParams(action, form) {
  const params = {};
  for (const p of action.params) {
    const v = form[p.name];
    if (p.kind === "flag" || p.type === "bool") {
      if (v) params[p.name] = true;
    } else if (p.kind === "bool_optional") {
      if (v === "true") params[p.name] = true;
      else if (v === "false") params[p.name] = false;
    } else if (v !== "" && v != null) {
      params[p.name] = v;
    }
  }
  return params;
}

function Field({ p, value, onChange }) {
  const lbl = `${p.label}${p.required ? " *" : ""}`;

  if (p.kind === "flag" || p.type === "bool") {
    return (
      <Checkbox
        label={lbl}
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ my: 0.5 }}
      />
    );
  }

  if (p.kind === "bool_optional") {
    return (
      <FormControl sx={{ my: 0.5 }}>
        <FormLabel>{lbl}</FormLabel>
        <Select value={value} onChange={(_, val) => onChange(val ?? "")}>
          <Option value="">— как по умолчанию —</Option>
          <Option value="true">Да</Option>
          <Option value="false">Нет</Option>
        </Select>
        {p.help && <FormHelperText>{p.help}</FormHelperText>}
      </FormControl>
    );
  }

  if (p.type === "choice") {
    return (
      <FormControl sx={{ my: 0.5 }}>
        <FormLabel>{lbl}</FormLabel>
        <Select value={value} onChange={(_, val) => onChange(val ?? "")}>
          {(p.choices || []).map((c) => (
            <Option key={c} value={c}>{c}</Option>
          ))}
        </Select>
        {p.help && <FormHelperText>{p.help}</FormHelperText>}
      </FormControl>
    );
  }

  const numeric = p.type === "int" || p.type === "float";
  const isLong = p.name === "wishes";
  return (
    <FormControl sx={{ my: 0.5 }}>
      <FormLabel>{lbl}</FormLabel>
      {isLong ? (
        <Textarea
          minRows={2}
          value={value}
          placeholder={p.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          type={numeric ? "number" : "text"}
          value={value}
          placeholder={p.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {p.help && <FormHelperText>{p.help}</FormHelperText>}
    </FormControl>
  );
}

export default function ActionsPage() {
  const { staff } = useAdminAuth();
  const role = staff?.role;

  const [groups, setGroups] = useState([]);
  const [tab, setTab] = useState(0);
  const [activeKey, setActiveKey] = useState(null);
  const [form, setForm] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminApi.actionsCatalog();
        if (!cancelled) setGroups(d.groups || []);
      } catch (e) {
        if (!cancelled) setLoadErr(e.message || "Не удалось загрузить список действий");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const actions = groups[tab]?.actions || [];
  const action = useMemo(
    () => actions.find((a) => a.key === activeKey) || null,
    [actions, activeKey]
  );

  const selectAction = (a) => { setActiveKey(a.key); setForm(initialForm(a)); setResult(null); };

  const missingRequired = action
    ? action.params.some((p) => p.required && (form[p.name] === "" || form[p.name] == null))
    : false;
  const needSuper = action?.min_role === "superadmin" && role !== "superadmin";

  const run = async () => {
    if (!action) return;
    if (action.danger === "high" &&
        !window.confirm(`Точно выполнить «${action.label}»? Это изменяющее действие.`)) return;
    setRunning(true);
    setResult(null);
    try {
      const data = await adminApi.actionsRun(action.key, buildParams(action, form));
      setResult(data);
    } catch (e) {
      setResult({ ok: false, error: e.message || "Ошибка запроса", output: "", took_ms: 0 });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (loadErr) {
    return <Alert color="danger" variant="soft">{loadErr}</Alert>;
  }

  return (
    <Box>
      <Typography level="h2" sx={{ mb: 0.5 }}>Доп. действия</Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
        Запуск сервисных команд из админки. Изменяющие действия по умолчанию идут в режиме пробного прогона.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setActiveKey(null); setResult(null); }}
        sx={{ bgcolor: "transparent" }}
      >
        <TabList>
          {groups.map((g, i) => <Tab key={g.group} value={i}>{g.group}</Tab>)}
        </TabList>
      </Tabs>

      {/* Действия текущей вкладки */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
        {actions.map((a) => {
          const selected = a.key === activeKey;
          return (
            <Card
              key={a.key}
              variant={selected ? "solid" : "outlined"}
              onClick={() => selectAction(a)}
              sx={{ cursor: "pointer", minWidth: 220, flex: "1 1 240px", maxWidth: 320, p: 1.5, gap: 0.5 }}
            >
              <Typography level="title-sm" sx={{ color: selected ? "common.white" : undefined }}>
                {a.label}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Chip size="sm" variant="soft" color={DANGER_COLOR[a.danger] || "neutral"}>
                  {DANGER_LABEL[a.danger] || a.danger}
                </Chip>
                <Chip size="sm" variant="soft" color="neutral">
                  {ROLE_LABEL[a.min_role] || a.min_role}
                </Chip>
              </Box>
            </Card>
          );
        })}
      </Box>

      {/* Форма выбранного действия */}
      {action && (
        <Sheet variant="outlined" sx={{ mt: 2, p: 2, borderRadius: "12px", maxWidth: 640 }}>
          <Typography level="title-md">{action.label}</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>{action.description}</Typography>
          <Divider sx={{ my: 1 }} />

          {action.params.map((p) => (
            <Field
              key={p.name}
              p={p}
              value={form[p.name]}
              onChange={(v) => setForm((f) => ({ ...f, [p.name]: v }))}
            />
          ))}

          {needSuper && (
            <Alert color="danger" variant="soft" sx={{ mt: 1 }}>
              Действие доступно только супер-администратору.
            </Alert>
          )}

          <Button
            onClick={run}
            loading={running}
            disabled={missingRequired || needSuper}
            color={DANGER_COLOR[action.danger] || "primary"}
            startDecorator={<PlayArrowRoundedIcon />}
            sx={{ mt: 1.5 }}
          >
            Выполнить
          </Button>

          {result && (
            <Box sx={{ mt: 2 }}>
              <Typography
                level="title-sm"
                sx={{ color: result.ok ? "success.plainColor" : "danger.plainColor" }}
              >
                {result.ok ? "✓ Готово" : "✗ Ошибка"}{" "}
                <Typography component="span" level="body-xs" color="neutral">
                  ({result.took_ms} мс)
                </Typography>
              </Typography>
              {result.error && (
                <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{result.error}</Typography>
              )}
              <Box
                component="pre"
                sx={{
                  m: 0, mt: 1, p: 1.5, borderRadius: "8px", bgcolor: "#1e1e1e", color: "#d4d4d4",
                  fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word",
                  overflow: "auto", maxHeight: 420,
                }}
              >
                {result.output || "(команда не вывела текста)"}
              </Box>
            </Box>
          )}
        </Sheet>
      )}
    </Box>
  );
}